import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { Grid, Button, TextField } from '@material-ui/core';
import { useGuestFields } from '../../../hooks/useGuestFields';
import { doSwaggerCall } from '../../../hooks/useSwaggerClient';
import {
    handleGuestFieldEventValue,
    validateSignupForm
} from '../../../components/modals/SignUpModal';
import BookingProgressBar from '../../../components/fancy/BookingProgressBar';
import { Link } from '@material-ui/core';
import { ErrorIcon } from '../../../icons';
import GuestFieldInput from '../../../components/GuestFieldInput';
import { formatPhone } from '../../../helpers/phoneUtils';

const PaymentUserRegistrationForm = ({ theme, setLoggedInUser }) => {
    const [apiError, setApiError] = useState('');
    const [guest_fields] = useGuestFields();

    const onSubmit = data => {
        setApiError('');
        // do the reg with the email address
        const req = doSwaggerCall('auth', 'signup', {
            userData: {
                firstname: data.firstname,
                lastname: data.lastname,
                email: data.email,
                phone: data.phone,
                password: `${Math.random()
                    .toString(36)
                    .substring(2, 15) +
                    Math.random()
                        .toString(36)
                        .substring(2, 15)}`,
                termsAndConditions: true,
                guest_field_info: data.guest_field_info
            }
        });
        req.then(data => {
            if (data.user) {
                setLoggedInUser(data.user);
            } else {
                setApiError(data.message || 'An error occurred during sign-up.');
            }
        })
            .catch(error => {
                console.error('Error during sign-up:', error.response.body.message);
                setApiError(error.response.body.message || 'An error occurred during sign-up.');
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const requiredGuestFields = guest_fields.filter(
        field => field?.agentOnly !== true && field?.required === true
    );

    const {
        values,
        isSubmitting,
        errors,
        isValid,
        dirty,
        handleSubmit,
        setFieldValue,
        setSubmitting
    } = useFormik({
        initialValues: {
            firstname: '',
            lastname: '',
            phone: '',
            email: '',
            guest_field_info: requiredGuestFields.map(field => ({
                id: field.id,
                name: field.name,
                value: ''
            }))
        },
        validate: values => validateSignupForm(values, requiredGuestFields),
        onSubmit: values => onSubmit(values)
    });

    useEffect(() => {
        setApiError('');
    }, [values]);

    const handleInputChange = (event, field) => {
        const { value } = event.target;
        setFieldValue(field, value);
    };

    return (
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2} className="page">
                <Grid item container md={8} xs={12} direction="column" justifyContent="flex-start">
                    <Link
                        to="/cart"
                        className="link mb-2"
                        style={{ color: theme.palette.primary.main }}
                    >
                        Back to Cart
                    </Link>
                    <h1 className="pageheader">Guest Information</h1>
                </Grid>
                <Grid item className="mb-2" md={4} xs={12}>
                    <BookingProgressBar activeStep={2} />
                </Grid>
                <Grid item xs={12} md={8}>
                    <h4>Please provide some basic information:</h4>
                    {apiError && (
                        <Grid
                            item
                            container
                            spacing={2}
                            xs={12}
                            style={{
                                color: theme.palette.error.main,
                                marginTop: '0.5rem'
                            }}
                            alignItems="center"
                        >
                            <Grid item>
                                <ErrorIcon color="error" />
                            </Grid>
                            <Grid item>
                                <span>{apiError}</span>
                            </Grid>
                        </Grid>
                    )}
                    <Grid container spacing={2} style={{ marginTop: '0.5rem' }}>
                        <Grid item xs={6}>
                            <TextField
                                label="First name"
                                required
                                type="text"
                                fullWidth
                                value={values.firstname}
                                onChange={ev => handleInputChange(ev, 'firstname')}
                                error={!!errors.firstname}
                                helperText={errors.firstname}
                                disabled={isSubmitting}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Last name"
                                required
                                type="text"
                                fullWidth
                                value={values.lastname}
                                onChange={ev => handleInputChange(ev, 'lastname')}
                                error={!!errors.lastname}
                                helperText={errors.lastname}
                                disabled={isSubmitting}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Contact Phone"
                                type="tel"
                                value={values.phone}
                                onChange={ev => handleInputChange(ev, 'phone')}
                                onBlur={() => {
                                    if (values.phone) {
                                        const formatted = formatPhone(values.phone);
                                        if (formatted !== values.phone) {
                                            setFieldValue('phone', formatted);
                                        }
                                    }
                                }}
                                disabled={isSubmitting}
                                error={!!errors.phone}
                                helperText={errors.phone}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Email"
                                value={values.email}
                                onChange={ev => handleInputChange(ev, 'email')}
                                disabled={isSubmitting}
                                error={!!errors.email}
                                helperText={errors.email}
                                fullWidth
                                required
                            />
                        </Grid>
                        {requiredGuestFields.map(field => (
                            <Grid item xs={12} sm={6} key={field.id}>
                                <GuestFieldInput
                                    field={field}
                                    values={values}
                                    errors={errors}
                                    setFieldValue={setFieldValue}
                                    handleEventValue={handleGuestFieldEventValue}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <br />
                    <br />
                    <Button
                        fullWidth={true}
                        className="btn-squared"
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting || !isValid || !dirty}
                    >
                        Continue
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

export default PaymentUserRegistrationForm;
